import { createContext, PropsWithChildren, useEffect, useState } from "react"
import { Cart, MainProduct, MainProductVariant, Screen } from "../../types"
import { areArraysEqual, getActiveMaterialByMainProductVariantId, getInitialActivePendantProductVariantIds, isEditMode, isSinglePendantProduct } from "../../utils"
import { getCorrespondingPendantProductVariantIdsByMaterial, getInitialEditProductVariantIds, getInitialUrlQueryProductVariantIds, updateUrlQueryProductVariantIds } from "./utils"

type AppContext = {
  mainProduct: MainProduct
  bufferedMainProduct: MainProduct | null
  setBufferedMainProduct: (product: MainProduct | null) => void
  bufferedMainProductVariant: MainProductVariant | null
  setBufferedMainProductVariant: (variant: MainProductVariant | null) => void
  linkedMainProducts: MainProduct[]
  cart: Cart
  activeScreen: Screen
  setActiveScreen: (screen: Screen) => void
  activeMainProductVariantId: string,
  setActiveMainProductVariantId: (id: string) => void
  activePendantProductVariantIds: string[]
  setActivePendantProductVariantIds: (id: string[]) => void
  active: number
  setActive: (index: number) => void
  activePendantProductId: string
  setActivePendantProductId: (activePendantProductId: string) => void
  designSummaryPopupOpen: boolean
  setDesignSummaryPopupOpen: (designSummaryPopupOpen: boolean) => void
  confirmationPopupDisplayed: boolean,
  setConfirmationPopupDisplayed: (displayed: boolean) => void
  redirectingToNextPage: boolean
  setRedirectingToNextPage: (redirecting: boolean) => void
  reset: () => void,
  isMainProductSinglePendant: boolean
}

const initialValue: AppContext = {
  mainProduct: {
    id: 0,
    available: false,
    title: "",
    handle: "",
    tags: [],
    price: 0,
    compareAtPriceMin: 0,
    options: [],
    collectionUrl: "",
    url: "",
    gallery: [],
    has_only_default_variant: false,
    images: [],
    templateSuffix: "",
    featured_image: "",
    variants: [],
    optionsWithValues: [],
    metafields: {
      type: "Necklace",
      builderData: {
        maxNumberOfPendants: 0,
      },
      pdpBiLine: "",
      pendantProducts: [],
      singlePendantProducts: [],
      dragAndDropEnabled: false
    },
    globalSettings: {
      dragAndDropEnabled: false,
      enableBauCtas: false,
      hasSizeGuide: false,
      sizeGuideDrawerId: '',
      findStoreUrl: '',
      firstAvailableVariantId: null,
      hasOnlyDefaultVariant: false,
      shopPermanentDomain: ''
    }
  },
  bufferedMainProduct: null,
  setBufferedMainProduct: () => null,
  bufferedMainProductVariant: null,
  setBufferedMainProductVariant: () => null,
  linkedMainProducts: [],
  cart: { items: [] },
  activeScreen: 'PENDANTS', 
  setActiveScreen: () => null,
  activeMainProductVariantId: "",
  setActiveMainProductVariantId: () => null,
  activePendantProductVariantIds: [],
  setActivePendantProductVariantIds: () => null,
  active: 0,
  setActive: () => null,
  activePendantProductId: "",
  setActivePendantProductId: () => null,
  designSummaryPopupOpen: false,
  setDesignSummaryPopupOpen: () => null,
  reset: () => null,
  confirmationPopupDisplayed: false,
  setConfirmationPopupDisplayed: () => null,
  redirectingToNextPage: false,
  setRedirectingToNextPage: () => null,
  isMainProductSinglePendant: false,
}

export const AppContext = createContext<AppContext>(initialValue)

type Props = PropsWithChildren & {
  mainProduct: MainProduct
  linkedMainProducts: MainProduct[]
  cart: Cart
  remounted?: boolean
}

export const AppProvider = ({ children, mainProduct, linkedMainProducts, cart, remounted }: Props) => {
  const initialUrlQueryProductVariantIds = getInitialUrlQueryProductVariantIds(mainProduct)
  const initialEditProductVariantIds = isEditMode() && !remounted ? getInitialEditProductVariantIds(mainProduct, cart) : null

  const isMainProductSinglePendant = isSinglePendantProduct(mainProduct)

  const initialActiveMainProductVariantId = mainProduct.variants.find(variant => variant.available)?.id

  if (!initialActiveMainProductVariantId) {
    throw new Error("No available main product variant found")
  }

  const initialActivePendantProductId = mainProduct.metafields.pendantProducts[0].id
  const initialPendantVariantIds = getInitialActivePendantProductVariantIds(mainProduct, initialActiveMainProductVariantId)  

  const userPendantVariantIds = getInitialActivePendantProductVariantIds(mainProduct, initialActiveMainProductVariantId)

  const [activeScreen, setActiveScreen] = useState<Screen>(remounted ? 'CHAIN' : 'PENDANTS')
  const [activeMainProductVariantId, setActiveMainProductVariantId] = useState(
    isEditMode() && !remounted && initialEditProductVariantIds
      ? initialEditProductVariantIds.chain
      : initialUrlQueryProductVariantIds
        ? initialUrlQueryProductVariantIds.chain
        : initialActiveMainProductVariantId
  )
  const [activePendantProductId, setActivePendantProductId] = useState(initialActivePendantProductId)
  const _activePendantProductVariantIds = isEditMode() && !remounted && initialEditProductVariantIds
    ? initialEditProductVariantIds.pendants
    : initialUrlQueryProductVariantIds
      ? initialUrlQueryProductVariantIds.pendants
      : userPendantVariantIds

  const [activePendantProductVariantIds, setActivePendantProductVariantIds] = useState<string[]>(
    _activePendantProductVariantIds
  )

  const initialSelectedPendantProductIndex = _activePendantProductVariantIds.length - 1

  const [selectedPendantProductIndex, setSelectedPendantProductIndex] = useState<number>(initialSelectedPendantProductIndex)
  const [designSummaryPopupOpen, setDesignSummaryPopupOpen] = useState(false)

  const [confirmationPopupDisplayed, setConfirmationPopupDisplayed] = useState<boolean>(false)
  const [redirectingToNextPage, setRedirectingToNextPage] = useState(false)
  const [bufferedMainProduct, setBufferedMainProduct] = useState<MainProduct | null>(null)
  const [bufferedMainProductVariant, setBufferedMainProductVariant] = useState<MainProductVariant | null>(null)

  const reset = () => {
    setSelectedPendantProductIndex(initialSelectedPendantProductIndex)
    setActiveMainProductVariantId(initialActiveMainProductVariantId)
    setActivePendantProductVariantIds(initialPendantVariantIds)
    setActiveScreen('PENDANTS')
  }

  useEffect(() => {
    const activeMaterial = getActiveMaterialByMainProductVariantId(mainProduct, activeMainProductVariantId)

    const correspondingPendantProductVariantIdsByMaterial = getCorrespondingPendantProductVariantIdsByMaterial(
      mainProduct, 
      activePendantProductVariantIds, 
      activeMaterial
    )

    const pendantVariantIdsWillChange = !areArraysEqual(correspondingPendantProductVariantIdsByMaterial, activePendantProductVariantIds)
    updateUrlQueryProductVariantIds(activeMainProductVariantId, activePendantProductVariantIds)
    
    if (pendantVariantIdsWillChange) {
      setActivePendantProductVariantIds(correspondingPendantProductVariantIdsByMaterial)
    }
  }, [activeMainProductVariantId])

  useEffect(() => {
    updateUrlQueryProductVariantIds(activeMainProductVariantId, activePendantProductVariantIds)
  }, [activePendantProductVariantIds])

  return (
    <AppContext.Provider value={{
      mainProduct,
      isMainProductSinglePendant,
      bufferedMainProduct,
      setBufferedMainProduct,
      bufferedMainProductVariant,
      setBufferedMainProductVariant,
      linkedMainProducts,
      cart,
      activeScreen,
      setActiveScreen,
      activeMainProductVariantId,
      setActiveMainProductVariantId,
      activePendantProductVariantIds,
      setActivePendantProductVariantIds,
      active: selectedPendantProductIndex,
      setActive: setSelectedPendantProductIndex,
      activePendantProductId,
      setActivePendantProductId,
      designSummaryPopupOpen,
      setDesignSummaryPopupOpen,
      reset,
      confirmationPopupDisplayed,
      setConfirmationPopupDisplayed,
      redirectingToNextPage,
      setRedirectingToNextPage,
    }}>
      { children }
    </AppContext.Provider>
  )
}
